import { Button } from '@neo/index'
import { Select, type SelectOption } from '@neo/primitives/select'
import type { ApiResponse } from '@shared/types'
import { useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { Navigate, useSearchParams } from 'router-v6'
import { useOrganizationsQuery, useSwitchOrganizationMutationV2 } from 'v2/pages/settings/queries'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { authService } from 'v2/shared/services/auth-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { useAppStore } from 'v2/state/app'
import type { RCLIExchangeResponse, RCLIRejectResponse } from 'v2/types/api/auth'
import { errorToast } from 'v2/utils/toasts'
import { AuthLayout } from '../../shared/layouts/auth'

// CLIAuthorize is the consent screen for the Plivo CLI's `plivo login`
// flow. The CLI redirects the user's browser to hodor's
// `/v1/accounts/cli/authorize`, which 302s here with `?state=…`. Hodor
// has already stashed (cb, code_challenge) under that state key.
//
// When the user clicks "Allow":
//   1. POST /v1/accounts/cli/exchange with {state} (Console session
//      bearer + aom_uuid are added by the standard authService
//      interceptors).
//   2. Receive {code, cb} from hodor.
//   3. Redirect the browser to `${cb}?state=…&code=…` so the CLI's
//      loopback listener (127.0.0.1:N) can capture the code and POST
//      hodor's /v1/accounts/cli/token to redeem the creds bundle.
//
// If the user clicks "Cancel" or this tab loads without a state param,
// we just navigate away — the CLI side will time out on its own listener
// (60s) and show a friendly error.
export const CLIAuthorize = () => {
  const [params] = useSearchParams()
  const { userAuthenticated, details } = useAuth()
  const state = params.get('state') ?? ''
  // Friendly machine label the CLI sent (e.g. "Mac MacBook-Pro").
  // Substitutes the generic "your machine" so users know exactly which
  // machine they're authorising. Hodor caps it at 128 chars and React
  // escapes JSX text by default, so no XSS / overflow surface.
  const deviceName = (params.get('device') ?? '').trim()
  const deviceLabel = deviceName || 'your machine'

  // Name of the org the console session is currently active on — this is
  // the org whose credentials the CLI receives if Allow is clicked right
  // now. The Aom_uuid header the CLI's request ultimately carries is
  // resolved server-side from this same session, so it always matches.
  const currentOrgName = details?.organization_name || 'your organization'

  // Full-page "Switching to <org>…" overlay (org-switch-loader.tsx, mounted
  // at the app root) is driven by this same store field — reusing it here
  // instead of local state gets that overlay for free, plus centralized
  // cleanup: useSwitchOrganizationMutationV2's onError already resets it.
  const switchingToOrgName = useAppStore((appState) => appState.switchingToOrgName)
  const setSwitchingToOrgName = useAppStore((appState) => appState.setSwitchingToOrgName)
  const isSwitchingOrg = switchingToOrgName !== null

  // A failed or empty org list must never block Allow — degrade to the
  // single-org display (queries.ts returns [] early when the session has
  // no region, and isError covers a real fetch failure).
  const { data: organizations, isError: hasOrganizationsError } = useOrganizationsQuery()
  const { mutate: switchOrganization } = useSwitchOrganizationMutationV2()

  const showOrgPicker = !hasOrganizationsError && (organizations?.length ?? 0) > 1

  const orgOptions: SelectOption[] = (organizations ?? []).map((org) => ({
    value: org.org_uuid,
    label: org.org_name,
  }))
  // Prefer the session's own org uuid when it has one: matching by name alone breaks
  // when two orgs share a name (find() always resolves to the first match, which may
  // not be the active org — then `value === selectedOrgUuid` in handleOrgChange
  // below short-circuits a click on the OTHER same-named org as a no-op "already
  // selected"). organization_uuid is optional on RUserSession — absent on sessions
  // stored before this field existed, and on backends that don't send it yet — so
  // fall back to the pre-existing name match in that case; that fallback carries the
  // same duplicate-name limitation as before, which is the best available without a
  // stable id.
  const currentOrgUuid = details?.organization_uuid
  const selectedOrgUuid = currentOrgUuid
    ? organizations?.find((org) => org.org_uuid === currentOrgUuid)?.org_uuid
    : organizations?.find((org) => org.org_name === currentOrgName)?.org_uuid

  const handleOrgChange = (value: string | string[]) => {
    if (typeof value !== 'string' || value === selectedOrgUuid) return
    const target = organizations?.find((org) => org.org_uuid === value)
    if (!target) return
    // A different Aom_uuid header alone cannot authorize a different org:
    // hodor's ValidateToken keys the session cache by (clientType,
    // clientVersion, aomUuid, browserSessionID), so the token is bound to
    // the org it was issued for. Genuinely switching orgs — which reloads
    // this same URL — is the only way to change which org gets authorized.
    setSwitchingToOrgName(target.org_name)
    switchOrganization({ org_uuid: target.org_uuid, region: target.region })
  }

  const exchangeMutation = useMutation({
    mutationFn: async () => {
      const res: ApiResponse<RCLIExchangeResponse> = await authService.cliExchange({ state })
      return res.data
    },
    onSuccess: (data: RCLIExchangeResponse) => {
      // Hand the browser off to the CLI's loopback listener. After this,
      // the CLI captures (state, code) and posts hodor's /cli/token with
      // the PKCE verifier to redeem the creds bundle.
      //
      // Build the target via the URL API so a cb that already carries a
      // query string (`/cb?foo=1`) or hash gets state+code appended
      // without producing a malformed `…?foo=1?state=…` double-`?`.
      // searchParams.set also handles encoding for us, so a state with
      // `&`/`+`/spaces is forwarded byte-correct.
      const target = new URL(data.cb)
      target.searchParams.set('state', state)
      target.searchParams.set('code', data.code)
      window.location.replace(target.toString())
    },
    onError: (err) => {
      // Surface hodor's `errors.global_error` instead of axios's generic
      // "Request failed with status N". getErrorText is the same helper
      // used by generateToastsForMutation across the app — keeps copy
      // consistent without forcing us to bolt onto its full onSuccess
      // (this flow doesn't want a success toast; it redirects).
      const errorText = getErrorText(err as AxiosError<ApiResponse<unknown>>)
      errorToast({
        title: 'Authorization failed',
        description: errorText
          ? `${errorText.replace(/^\d+\.\s*/, '')}. You can close this tab and retry — the CLI will time out and show a clear error.`
          : 'Could not authorize the Plivo CLI. You can close this tab and retry — the CLI will time out and show a clear error.',
      })
    },
  })

  const leaveWithoutAuthorizing = () => {
    if (window.opener) {
      window.close()
    } else {
      window.location.href = '/'
    }
  }

  // Declining has to reach the CLI. Closing the tab alone left it listening
  // for five minutes (not 60s, as an earlier comment here claimed) and then
  // failing with a timeout that told the user to go and approve the thing they
  // had just refused.
  //
  // /cli/reject also deletes the state server-side, so the authorize link dies
  // instead of staying live for the rest of its 10-minute TTL.
  const rejectMutation = useMutation({
    mutationFn: async () => {
      const res: ApiResponse<RCLIRejectResponse> = await authService.cliReject({ state })
      return res.data
    },
    onSuccess: (data: RCLIRejectResponse) => {
      // access_denied is OAuth's denial code (RFC 6749 §4.1.2.1); the CLI
      // matches on it. No cb means the state was already gone, so there is no
      // listener waiting and closing the tab is all that is left.
      if (!data?.cb) {
        leaveWithoutAuthorizing()
        return
      }
      const target = new URL(data.cb)
      target.searchParams.set('state', state)
      target.searchParams.set('error', 'access_denied')
      window.location.replace(target.toString())
    },
    // A failed reject must not trap the user on this page. The state expires on
    // its own, so leaving is always safe.
    onError: () => leaveWithoutAuthorizing(),
  })

  // Unauthenticated → push through normal login and resume on this URL.
  // login-v2.tsx reads `?next_url=`; matching the key is what makes the
  // resume work after the user completes the email/password (+ 2FA) flow.
  if (!userAuthenticated) {
    const nextUrl = encodeURIComponent(`/cli/authorize?state=${state}`)
    return <Navigate to={`/login?next_url=${nextUrl}`} replace />
  }

  // Missing or empty state — the URL was opened directly, not from hodor's
  // /authorize redirect. Send the user home.
  if (!state) {
    return <Navigate to="/" replace />
  }

  const handleCancel = () => rejectMutation.mutate()

  return (
    <AuthLayout
      pageTitle="Authorize Plivo CLI"
      title="Authorize the Plivo CLI"
      description={`The plivo command-line tool on ${deviceLabel} is requesting access to sign in as you.`}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-p-400 text-secondary">Authorizing access for</span>
          {showOrgPicker ? (
            <div data-testid="cli-authorize-org-select" className="w-[220px]">
              <Select
                name="cli-authorize-org-select"
                options={orgOptions}
                value={selectedOrgUuid}
                onChange={handleOrgChange}
                disabled={isSwitchingOrg}
                placeholder="Select organization"
              />
            </div>
          ) : (
            <span className="text-p-500 text-primary" data-testid="cli-authorize-org-name">
              {currentOrgName}
            </span>
          )}
        </div>

        <div className="text-p-400 text-secondary space-y-3">
          <p>
            Approving will let the Plivo CLI access your Plivo numbers, calls, messages, and AI
            debugging tools on your behalf, using your Plivo Account key.
          </p>
          <p>
            Only approve if you just ran{' '}
            <code className="text-p-400 text-primary bg-st1 rounded px-1.5 py-0.5">
              plivo login
            </code>{' '}
            in your terminal on {deviceLabel}.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button
            variant="default"
            size="lg"
            onClick={() => exchangeMutation.mutate()}
            disabled={exchangeMutation.isPending || rejectMutation.isPending || isSwitchingOrg}
            data-testid="cli-authorize-allow-btn"
            className="w-full sm:w-auto"
          >
            {exchangeMutation.isPending ? 'Authorizing…' : 'Allow'}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={handleCancel}
            disabled={exchangeMutation.isPending || rejectMutation.isPending || isSwitchingOrg}
            data-testid="cli-authorize-cancel-btn"
            className="w-full sm:w-auto"
          >
            {rejectMutation.isPending ? 'Cancelling…' : 'Cancel'}
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}
